import {
  Box,
  Divider,
  IconButton,
  PlainTooltip,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import Link from 'next/link';
import { twJoin } from 'tailwind-merge';
import { memo } from 'react';

import ArticleAuthor from './ArticleAuthor';

import { CollectionArticleItemProps } from '@/shared/types';
import { convertDateString } from '@/shared/utils';
import { ScrapIconButton } from '@/shared/Components';
import { PATH } from '@/shared/constants';

const CollectionArticleItem = ({
  id,
  href,
  title,
  author,
  isOwner,
  category,
  thumbnail,
  description,
  dateCreated,
  categoryHref,
  collectionTitle,
  isExternalContent,
  isVisibleCategory,
  isVisibleUtil = true,
  onClickDelete,
}: CollectionArticleItemProps) => {
  return (
    <article className="flex w-full flex-col gap-2">
      {isVisibleCategory && (
        <Box className="mb-1.5 h-6">
          <Link href={categoryHref} data-testid="contentsItem-category-link">
            <Typography
              variant="body2"
              className="text-w-gray-500 hover:underline"
            >
              {category}
            </Typography>
          </Link>
        </Box>
      )}
      <Link href={href} data-testid="contentsItem-item-link" target="_blank">
        <Box className="group flex justify-between gap-4 tablet:gap-12">
          <Box className="group flex w-[calc(100%_-_100px)] justify-between gap-4 tablet:w-full tablet:gap-12">
            <Box className="flex w-full flex-col gap-3">
              <Box className="flex flex-col gap-2 tablet:gap-1">
                <Typography
                  variant="body1"
                  className="line-clamp-2 font-medium text-w-gray-900 group-hover:text-w-gray-600 tablet:line-clamp-1 tablet:typo-subTitle1"
                >
                  {title}
                </Typography>
                <Box className="flex items-center">
                  {collectionTitle && (
                    <>
                      <Typography
                        variant="body2"
                        className="truncate break-words text-w-gray-700"
                      >
                        {collectionTitle}
                      </Typography>
                      <Divider className="mx-2 h-[13px]" isVertical />
                    </>
                  )}
                  <Typography
                    variant="body2"
                    className="w-fit whitespace-nowrap text-w-gray-700"
                  >
                    {`${convertDateString(dateCreated, 'yyyy.mm.dd')} 저장`}
                  </Typography>
                </Box>
                <Box
                  className="hidden tablet:block"
                  data-testid="contentsItem-description"
                >
                  <Typography
                    variant="body2"
                    className="line-clamp-2 text-w-gray-500"
                  >
                    {description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            className={twJoin(
              'flex-none duration-200 ease-in',
              'desktop:group-hover:-translate-y-[5%]'
            )}
          >
            <img
              alt={title}
              src={
                thumbnail ||
                `${PATH.MEDIA_YOZM_IMAGE}/default_collection_thumbnail_square.svg`
              }
              loading="eager"
              className="h-[84px] w-[84px] rounded-2xl border border-w-gray-100 object-cover tablet:h-[110px] tablet:w-[110px]"
              onError={(e) => {
                e.currentTarget.src = `${PATH.MEDIA_YOZM_IMAGE}/default_collection_thumbnail_square.svg`;
              }}
            />
          </Box>
        </Box>
      </Link>
      {isVisibleUtil && (
        <Box className="flex items-center justify-between tablet:mr-[158px]">
          {!isExternalContent && <ArticleAuthor {...author} />}
          <Box className="flex flex-1 justify-end gap-1">
            {!isExternalContent && (
              <ScrapIconButton
                data-testid="articleItem-scrap-button"
                id={id}
                isScrapBoxPositionLeft={isOwner}
              />
            )}
            {isOwner && (
              <PlainTooltip text="삭제">
                <IconButton
                  size="md"
                  data-testid="articleItem-delete-button"
                  onClick={() => onClickDelete && onClickDelete(id)}
                >
                  <SystemIcon name="medium_minus" />
                </IconButton>
              </PlainTooltip>
            )}
          </Box>
        </Box>
      )}
    </article>
  );
};

export default memo(CollectionArticleItem);
